// AIDEV-NOTE: Re-export meditation types from database for feature use
// Additional feature-specific types can be added here

export type {
    TMeditation,
    TMeditationInsert,
    TSignedMeditation,
} from "~/database/types";

export type MeditationType = "intro" | "session" | "path" | "part";
export type MeditationPauseMultiplier = 1 | 2 | 3;
export type MeditationOpening = "full" | "brief";
export type MeditationStatus =
    | "PENDING"
    | "IN_PROGRESS"
    | "COMPLETED"
    | "ERROR"
    // AIDEV-NOTE: Cancel-on-close — generation aborted, listener gone. Terminal.
    | "CANCELLED";

// AIDEV-NOTE: Terminal outcome of the first generation+listen (write-once).
// See meditationOutcomeEnum in schema.ts and deriveMeditationOutcome().
export type MeditationOutcome =
    | "completed"
    | "stopped"
    | "abandoned"
    | "never_started"
    | "empty_audio"
    | "playback_error"
    | "generation_failed";

// AIDEV-NOTE: Sentinel voice value for a practitioner's custom (cloned) voice.
// The server resolves it to the account-scoped ElevenLabs voiceId at generation
// time (see meditation-generator.server.ts); the picker injects it as the default
// option only when a custom voice is available for the account.
export const CUSTOM_VOICE_VALUE = "custom";

// AIDEV-NOTE: A client may be linked to several practitioners who each have a voice,
// so the sentinel became a family: bare "custom" (legacy rows + "my own clone") plus
// "custom:<practitionerProfileId>" for a specific practitioner's voice.
//
// Keyed on the practitioner's PROFILE ID, not the provider voiceId, because this value
// is persisted in localStorage, appears in ?voice= URLs and is written to
// meditation.voice — it must survive the practitioner re-cloning, and must not leak a
// third-party provider id to clients.
//
// Backward compatible by construction: audio cache slots are keyed `custom-{voiceId}`
// (see resolveCacheVoiceName), not on the sentinel, so a remembered "custom" migrating
// to "custom:<id>" resolves to the same voiceId, the same storage path, and therefore
// still hits existing cached audio. Existing meditation.voice rows need no migration.
export const CUSTOM_VOICE_PREFIX = "custom:";

export type CustomVoiceValue =
    | typeof CUSTOM_VOICE_VALUE
    | `${typeof CUSTOM_VOICE_PREFIX}${string}`;

export function isCustomVoice(voice: string): voice is CustomVoiceValue {
    // AIDEV-NOTE: A bare "custom:" with no id is NOT a valid custom voice — it can never
    // resolve to a practitioner. Requiring a non-empty suffix keeps the predicate and
    // parseCustomVoiceKey in agreement; they used to disagree, which let a malformed
    // value slip past resolveCacheVoiceName's guard and into a storage key.
    return (
        voice === CUSTOM_VOICE_VALUE ||
        (voice.startsWith(CUSTOM_VOICE_PREFIX) &&
            voice.length > CUSTOM_VOICE_PREFIX.length)
    );
}

export function customVoiceKeyFor(
    practitionerProfileId: string,
): CustomVoiceValue {
    return `${CUSTOM_VOICE_PREFIX}${practitionerProfileId}`;
}

/** Practitioner profile id from a "custom:<id>" value; null for bare "custom". */
export function parseCustomVoiceKey(voice: string): string | null {
    if (!voice.startsWith(CUSTOM_VOICE_PREFIX)) return null;
    return voice.slice(CUSTOM_VOICE_PREFIX.length) || null;
}

// AIDEV-NOTE: Collapse any custom-voice sentinel to bare "custom" for analytics.
// The per-practitioner variant carries a profile UUID, which must not land in event
// props: it is an identifier, and it would explode the `voice` dimension's cardinality.
export function voiceForAnalytics(
    voice: string,
): MeditationVoice | typeof CUSTOM_VOICE_VALUE {
    // AIDEV-NOTE: Cast is safe for catalog values — callers only ever pass a value
    // that came out of the picker's own option list.
    return isCustomVoice(voice)
        ? CUSTOM_VOICE_VALUE
        : (voice as MeditationVoice);
}

// AIDEV-NOTE: Voice options for meditation generation — ElevenLabs TTS only.
// Adding a voice? Also map it in voice-config.server.ts and run
// scripts/generate-voice-previews.ts against staging AND production before release so
// the picker's play button has a cached sample (see that script's header).
export const MEDITATION_VOICES = [
    { value: "Brad", label: "Brad Voice" },
    { value: "Eryn", label: "Eryn Voice" },
    { value: "Locke", label: "Locke Voice" },
    { value: "Marcel", label: "Marcel Voice" },
] as const;

// AIDEV-NOTE: Voices gated on `hwa-launch` — the Healing Works Academy RELEASE
// kill-switch, not an entitlement. Tamala briefly sat in RESOURCES_MEDITATION_VOICES
// (keyed on `enable-dpmp`), where she vanished for anyone without the DPMP package, and
// then in the base catalog ungated while HWA was being demoed. She belongs on HWA's own
// flag: her voice has nothing to do with DPMP, and it must go dark with her course so
// Parts Companion can ship to production without publishing it.
export const HWA_MEDITATION_VOICES = [
    { value: "Tamala", label: "Tamala Floyd" },
] as const;

// AIDEV-NOTE: Voices gated behind the `enable-dpmp` package flag (user opt-in via
// Settings → Add-Ons; formerly `show-resources-navigation`). Michelle Glass's cloned
// voice is offered as a standard option only to accounts that enabled the DPMP®
// package — the picker includes these solely when the flag is on
// (see useMeditationVoiceOptions). Server resolution/replay treat them as regular
// catalog voices via ALL_MEDITATION_VOICES. Adding a voice here also needs a preview
// pre-warm (scripts/generate-voice-previews.ts) in staging and production.
export const RESOURCES_MEDITATION_VOICES = [
    { value: "Michelle", label: "Michelle Glass" },
] as const;

// AIDEV-NOTE: Full catalog — base, DPMP-package-gated (RESOURCES_MEDITATION_VOICES) AND
// release-gated (HWA_MEDITATION_VOICES, behind hwa-launch). Deliberately ignores every
// gate: use this for server-side name↔value resolution and replay, where a voice already
// stamped on a stored meditation must keep resolving even if the gate that offered it is
// since off. Picker surfaces must use getAvailableMeditationVoices() instead.
export const ALL_MEDITATION_VOICES = [
    ...MEDITATION_VOICES,
    ...RESOURCES_MEDITATION_VOICES,
    ...HWA_MEDITATION_VOICES,
] as const;

export type MeditationVoice =
    | (typeof MEDITATION_VOICES)[number]["value"]
    | (typeof RESOURCES_MEDITATION_VOICES)[number]["value"]
    | (typeof HWA_MEDITATION_VOICES)[number]["value"];

// AIDEV-NOTE: Picker value type — static catalog voices plus any custom-voice
// sentinel (bare "custom", or "custom:<practitionerId>" for a specific practitioner).
export type SelectableVoice = MeditationVoice | CustomVoiceValue;

export interface MeditationVoiceChoice {
    value: MeditationVoice;
    label: string;
}

/**
 * Get available meditation voices for a picker.
 *
 * Gated voices are opt-in per flag: `includeResourcesVoices` wires to `enable-dpmp`
 * (Michelle Glass), `includeHwaVoices` to `hwa-launch` (Tamala Floyd). Server-side
 * name-to-id resolution must use ALL_MEDITATION_VOICES instead, so a voice already
 * persisted on a meditation keeps resolving after a flag is turned off.
 */
export function getAvailableMeditationVoices(options?: {
    includeResourcesVoices?: boolean;
    includeHwaVoices?: boolean;
}): readonly MeditationVoiceChoice[] {
    return [
        ...MEDITATION_VOICES,
        ...(options?.includeResourcesVoices ? RESOURCES_MEDITATION_VOICES : []),
        ...(options?.includeHwaVoices ? HWA_MEDITATION_VOICES : []),
    ];
}
